/**
 * Perkie Cart Bridge
 *
 * Standalone script (NOT part of the Preact bundle) that bridges
 * the previewer component to Shopify's native product form.
 *
 * Listens for DOM custom events from the Preact previewer and
 * injects/removes hidden line item property inputs into the
 * product form so they're submitted with the Add to Cart action.
 *
 * Events handled:
 *   perkie:style-selected  — inject/update all line item properties
 *   perkie:previewer-reset  — clear properties, disable Add to Cart
 *   perkie:previewer-ready  — initial setup (disable button until style chosen)
 *   perkie:font-selected    — inject/update font line item properties
 */
(function () {
  "use strict";

  // ── Double-load guard (2026-08-10) ─────────────────────────────────
  // Replaces the global window.addEventListener proxy that used to live in
  // snippets/perkie-product-ux-fixes.liquid. That proxy allowed only ONE
  // handler per `perkie:` event type page-wide, so any other script that
  // legitimately listened for the same type before this file loaded (the
  // Port Step 1 reveal gate does exactly that) silently stole the bridge's
  // registration. Guard the actual concern — this file executing twice —
  // and leave the event namespace open to multiple listeners.
  if (window.__perkieCartBridgeLoaded) return;
  window.__perkieCartBridgeLoaded = true;

  // ── Selectors ──────────────────────────────────────────────────────
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  const CONTAINER_SELECTOR = ".perkie-cart-properties";
  const SUBMIT_SELECTOR = ".product-form__submit";
  const STYLE_INDICATOR_SELECTOR = ".perkie-style-indicator";

  // ── Style label map ────────────────────────────────────────────────
  const STYLE_LABELS = {
    bw: "Black & White",
    color: "Color",
    ink: "Ink Wash",
    marker: "Marker",
  };

  // ── Helpers ────────────────────────────────────────────────────────

  /**
   * Find the product form on the page.
   * Scoped to product pages using the custom template.
   */
  function getForm() {
    return document.querySelector(FORM_SELECTOR);
  }

  /**
   * 2026-05-11 fix (Audit 2): silent-failure telemetry.
   *
   * Every cart-bridge handler silently no-ops when getForm() misses (i.e.
   * the FORM_SELECTOR stops matching — Dawn upgrade, theme edit, etc.).
   * That class of bug shows up only as customer email reports. Log once
   * per page load + emit a GA4 event so Daily Pulse can flag it before
   * customers report.
   */
  var formNotFoundLogged = false;
  function reportFormNotFound(handlerName) {
    if (formNotFoundLogged) return;
    formNotFoundLogged = true;
    try {
      console.warn(
        "[perkie-cart-bridge] form selector did not match — ATC won't be wired",
        { selector: FORM_SELECTOR, handler: handlerName, pathname: window.location.pathname }
      );
    } catch (e) {}
    if (typeof window.gtag === "function") {
      try {
        window.gtag("event", "pp_cart_bridge_form_not_found", {
          handler: handlerName,
          pathname: window.location.pathname,
        });
      } catch (e) {}
    }
  }

  /**
   * Find or create the hidden input container inside the form.
   */
  function getContainer(form) {
    let container = form.querySelector(CONTAINER_SELECTOR);
    if (!container) {
      container = document.createElement("div");
      container.className = "perkie-cart-properties";
      container.style.display = "none";
      // Insert before the buttons div
      const buttons = form.querySelector(".product-form__buttons");
      if (buttons) {
        form.insertBefore(container, buttons);
      } else {
        form.appendChild(container);
      }
    }
    return container;
  }

  /**
   * Create a hidden input element for a line item property.
   */
  function createInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "properties[" + name + "]";
    input.value = value || "";
    input.setAttribute("data-perkie-property", "true");
    return input;
  }

  /**
   * D3-PW0 (Port Step 34, R1 ruled 2026-09-27): "confirmed" means put on a
   * piece in the cart. The previewer never sees the add-to-cart submit, so
   * the write lives here, on BOTH submit paths (the capture-phase multi-pet
   * intercept and the bubble-phase single-pet listener), once per submit.
   * Reads every _perkie_pet_N_upload_id the style-selected emission put in
   * the form, adds each to localStorage `perkie_confirmed_pets` — the same
   * shape frontend/src/hooks/useUpload.js markPetConfirmed writes (an array
   * of uploadIds) — idempotently. No network, no events; a no-op when
   * localStorage is unavailable. Runs BEFORE anything that navigates.
   * The form attribute guards a submit that reaches both listeners; it is
   * released on the next tick so a later submit writes again (harmless —
   * the write is idempotent).
   */
  var CONFIRMED_PETS_KEY = "perkie_confirmed_pets";
  function markPetsConfirmedFromForm(form) {
    try {
      if (!form || form.hasAttribute("data-perkie-confirmed")) return;
      var styleInput = form.querySelector('input[name="properties[_perkie_style]"]');
      if (!styleInput || !styleInput.value) return; // not a perkie line
      var ids = [];
      var inputs = form.querySelectorAll("input[name^='properties[_perkie_pet_']");
      for (var i = 0; i < inputs.length; i++) {
        var m = /^properties\[_perkie_pet_\d+_upload_id\]$/.exec(inputs[i].name || "");
        if (m && inputs[i].value) ids.push(inputs[i].value);
      }
      if (!ids.length) return;
      form.setAttribute("data-perkie-confirmed", "true");
      setTimeout(function () { form.removeAttribute("data-perkie-confirmed"); }, 0);
      var raw = null;
      try { raw = localStorage.getItem(CONFIRMED_PETS_KEY); } catch (e) { return; }
      var list = [];
      try { var parsed = raw ? JSON.parse(raw) : []; if (Array.isArray(parsed)) list = parsed; } catch (e) { list = []; }
      var changed = false;
      for (var j = 0; j < ids.length; j++) {
        if (list.indexOf(ids[j]) === -1) { list.push(ids[j]); changed = true; }
      }
      if (changed) localStorage.setItem(CONFIRMED_PETS_KEY, JSON.stringify(list));
    } catch (e) {
      /* fails closed: nothing written, nothing thrown into the submit */
    }
  }

  /**
   * Remove all perkie property inputs from the form.
   */
  function clearProperties(form) {
    if (!form) return;
    const inputs = form.querySelectorAll("[data-perkie-property]");
    inputs.forEach(function (input) {
      input.remove();
    });
  }

  /**
   * Get the submit button for the form.
   */
  function getSubmitButton(form) {
    if (!form) return null;
    // Walk up to product-form custom element, then find button
    const productForm = form.closest("product-form");
    if (productForm) {
      return productForm.querySelector(SUBMIT_SELECTOR);
    }
    return form.querySelector(SUBMIT_SELECTOR);
  }

  /**
   * F1 (2026-07-19): stage-aware gate copy. The previewer broadcasts its
   * buy-box machine state via perkie:progress; the gated ATC label always
   * reflects the ONE thing actually blocking the customer. Previously the
   * label was a static "Upload a photo to continue" from upload through
   * style selection — flatly wrong once a photo existed (one of the
   * audit's four contradictory states, ~46 stall-clicks/mo).
   *
   * The bridge NEVER enables the button from perkie:progress — enable
   * authority stays with perkie:style-selected / perkie:previewer-reset.
   */
  var GATE_LABELS = {
    "no-photo": "Upload a photo to continue",
    "needs-name": "Add your pet's name to continue",
    "generating": "Creating your previews…",
    "needs-pets": "Finish adding your pets",
    "needs-style": "Choose a style to continue",
  };
  var lastProgressStage = "no-photo";

  function gateLabel() {
    return GATE_LABELS[lastProgressStage] || GATE_LABELS["no-photo"];
  }

  function onProgress(event) {
    var stage = event.detail && event.detail.stage;
    if (!stage) return;
    lastProgressStage = stage;
    var form = getForm();
    if (!form) return;
    var btn = getSubmitButton(form);
    if (!btn) return;
    // Never touch a button Shopify itself disabled (e.g. "Sold out") —
    // relabeling it would mask the real reason (review finding, F1).
    if (btn.disabled) return;
    // Only act while the perkie gate is active. "ready" has no gate
    // label — style-selected will enable and relabel.
    if (!btn.hasAttribute("data-perkie-gated")) return;
    // Stamp the stage first so tap-routing stays consistent even for
    // stages without a label ("ready").
    btn.setAttribute("data-perkie-gate-stage", stage);
    var label = GATE_LABELS[stage];
    if (!label) return;
    var span = btn.querySelector("span");
    if (span) span.textContent = label;
  }

  /**
   * Gate the Add to Cart button with a custom message — SOFT gate (T2, 2026-06-09).
   *
   * Previously this set btn.disabled = true, which made every tap on
   * "Upload a photo to continue" a silent no-op (Clarity recorded 22
   * sessions bumping into it; 7 bounced without ever uploading — see
   * pdp-redesign/data-findings-and-tasks-2026-06-09.md). The button now
   * stays live: tapping it opens the upload picker AND scrolls to the
   * previewer, mirroring the sticky ATC bar's gated behavior. A real
   * `disabled` attribute is reserved for Shopify's own reasons (sold out).
   */
  function disableSubmit(form, message) {
    const btn = getSubmitButton(form);
    if (!btn) return;
    btn.disabled = false;
    btn.setAttribute("data-perkie-gated", "");
    btn.setAttribute("data-perkie-gate-stage", lastProgressStage);
    const span = btn.querySelector("span");
    if (span && message) {
      // Only save original text if we haven't already (prevents double-disable overwriting)
      if (!btn.hasAttribute("data-perkie-original-text")) {
        btn.setAttribute("data-perkie-original-text", span.textContent);
      }
      span.textContent = message;
    }
    attachGateGuard(btn, form);
  }

  /**
   * Capture-phase click guard for the soft gate. Runs synchronously
   * inside the user gesture so iOS Safari allows the file picker.
   */
  function attachGateGuard(btn, form) {
    if (btn.hasAttribute("data-perkie-gate-guard")) return;
    btn.setAttribute("data-perkie-gate-guard", "");
    btn.addEventListener("click", function (e) {
      if (!btn.hasAttribute("data-perkie-gated")) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      // F1 (2026-07-19): route the gated tap to the actual blocker.
      // Opening the OS file picker is only right when there's no photo
      // yet — doing it after upload risks an accidental re-upload.
      var stage = btn.getAttribute("data-perkie-gate-stage") || "no-photo";
      // A gated tap is intent to buy that the funnel could not honour yet.
      // Without its own event it is indistinguishable from a real
      // add-to-cart click in analytics.
      try {
        document.dispatchEvent(
          new CustomEvent("perkie:gated-tap", { detail: { stage: stage } })
        );
      } catch (e) { /* instrumentation is best-effort */ }
      if (stage === "no-photo") {
        openUploadPicker();
        scrollToPreviewerFromBridge();
      } else if (stage === "needs-name") {
        scrollToPreviewerFromBridge();
        focusPetNameFromBridge();
      } else {
        // generating / needs-style / needs-pets: send them to the widget
        scrollToPreviewerFromBridge();
      }
    }, true);
    // Belt-and-braces: block implicit form submission (e.g. Enter key)
    // while gated, with the same redirect-to-previewer behavior.
    if (form && !form.hasAttribute("data-perkie-gate-guard")) {
      form.setAttribute("data-perkie-gate-guard", "");
      form.addEventListener("submit", function (e) {
        const b = getSubmitButton(form);
        if (b && b.hasAttribute("data-perkie-gated")) {
          e.preventDefault();
          e.stopImmediatePropagation();
          scrollToPreviewerFromBridge();
        }
      }, true);
    }
  }

  /**
   * Open the OS file picker by clicking the previewer's upload zone.
   * Delegating to the zone (not the hidden input directly) preserves
   * the previewer's own analytics + tap-feedback handling.
   */
  function openUploadPicker() {
    var zone = document.querySelector("[data-perkie-previewer] .perkie-upload-zone");
    if (zone) zone.click();
  }

  /**
   * F1 (2026-07-19): ask the previewer to highlight + focus the pet-name
   * field. The previewer handles this by setting its red error state AND
   * focusing — so even when iOS declines to open the keyboard outside
   * the gesture stack, the customer still gets a visible cue.
   */
  function focusPetNameFromBridge() {
    window.dispatchEvent(new CustomEvent("perkie:request-name-focus"));
  }

  /**
   * Scroll the customer up to the previewer, offset for the sticky header.
   */
  function scrollToPreviewerFromBridge() {
    var previewer = document.querySelector("[data-perkie-previewer]");
    if (!previewer) return;
    var header =
      document.querySelector("sticky-header") ||
      document.querySelector(".section-header") ||
      document.querySelector("header");
    var headerH = header ? header.getBoundingClientRect().height : 0;
    var top = previewer.getBoundingClientRect().top + window.pageYOffset - headerH - 12;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }

  /**
   * Pulse the Add to Cart button so the customer knows their style
   * has been confirmed. Called after style-selected is handled.
   *
   * Phase 3.6 (May 2026): no longer scrolls the inline ATC into view.
   * Phase 3 added a sticky ATC bar that's always visible on mobile/
   * tablet, so the scroll-to-ATC was redundant AND actively hurt UX:
   * it jumped customers past the previewer's confirmed-pet state and
   * the "Add another pet" CTA, reducing multi-pet AOV. Both paths
   * benefit:
   *   - Auto-advance from /free-preview: customer lands at top with
   *     Pet 1 confirmed, sees the previewer state, can add Pet 2
   *   - In-page style pick: customer is already looking at the
   *     previewer; pulse + sticky ATC is enough feedback
   */
  function scrollToAndPulseSubmit(form) {
    var btn = getSubmitButton(form);
    if (!btn) return;
    // Small delay so the style indicator renders before we pulse
    setTimeout(function () {
      btn.classList.add("perkie-atc-pulse");
      btn.addEventListener("animationend", function () {
        btn.classList.remove("perkie-atc-pulse");
      }, { once: true });
    }, 350);
  }

  /**
   * Enable the Add to Cart button, restoring original text.
   */
  function enableSubmit(form) {
    const btn = getSubmitButton(form);
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute("data-perkie-gated");
    btn.removeAttribute("data-perkie-gate-stage"); // F1: don't leave a stale stage
    const span = btn.querySelector("span");
    const originalText = btn.getAttribute("data-perkie-original-text");
    if (span && originalText) {
      span.textContent = originalText;
      btn.removeAttribute("data-perkie-original-text");
    }
  }

  /**
   * Update or create the visual style indicator above the buy button.
   */
  function updateStyleIndicator(form, detail) {
    if (!form) return;
    const buttons = form.querySelector(".product-form__buttons");
    if (!buttons) return;

    let indicator = form.querySelector(STYLE_INDICATOR_SELECTOR);
    if (!indicator) {
      indicator = document.createElement("div");
      indicator.className = "perkie-style-indicator";
      buttons.parentNode.insertBefore(indicator, buttons);
    }

    const styleLabel = detail.styleLabel || STYLE_LABELS[detail.style] || detail.style;
    const pets = detail.pets || [];
    const petNames = pets
      .map(function (p) { return p.name; })
      .filter(Boolean);
    const previewUrl = pets.length > 0 ? pets[0].previewUrl : null;

    let html = '<div class="perkie-style-indicator__inner">';

    if (previewUrl) {
      html += '<img class="perkie-style-indicator__thumb" src="' + previewUrl + '" alt="Preview" width="48" height="48" />';
    }

    html += '<div class="perkie-style-indicator__info">';
    html += '<span class="perkie-style-indicator__style">' + styleLabel + " Style</span>";

    if (petNames.length > 0) {
      html += '<span class="perkie-style-indicator__pets">';
      html += petNames.length === 1
        ? "Pet: " + petNames[0]
        : "Pets: " + petNames.join(", ");
      html += "</span>";
    }

    html += "</div></div>";

    indicator.innerHTML = html;
    indicator.style.display = "";
  }

  /**
   * Hide the style indicator.
   */
  function hideStyleIndicator(form) {
    if (!form) return;
    const indicator = form.querySelector(STYLE_INDICATOR_SELECTOR);
    if (indicator) {
      indicator.style.display = "none";
      indicator.innerHTML = "";
    }
  }

  // ── Additional Pet Fee ─────────────────────────────────────────────

  /**
   * Fee variant map: { extraPetCount: variantId }
   * Parsed from data-perkie-fee-variants on the previewer element.
   * e.g. { "1": 12345, "2": 67890 }
   */
  var feeVariantMap = {};
  var currentPetCount = 1;
  // Port Step 32-B (D10, ruled 2026-09-24 R1): the fee counts distinct
  // SOURCE PHOTOS, not pets — it pays for editing and combining separate
  // photos into one image, and two pets split from one photo need none.
  // Read from the previewer's emissions (detail.photoCount, or the pets'
  // sourcePhotoId); a widget that sends neither leaves photos === pets, so
  // today's charge is unchanged. `_perkie_pet_count` stays the PET count
  // (the webhook reads pets from it); the fee VARIANT is keyed on photos.
  var currentPhotoCount = 1;
  function photoCountFromPets(pets) {
    var ids = {};
    var n = 0;
    var missing = 0;
    for (var i = 0; i < pets.length; i++) {
      var p = pets[i] || {};
      var id = p.sourcePhotoId || p.uploadId;
      if (!id) { missing += 1; continue; }
      if (!ids[id]) { ids[id] = true; n += 1; }
    }
    return n + missing;
  }

  // Port Step 3 (spec v7 §1.2): how the pets print, from the previewer's
  // style-selected detail. "together" = one multi-pet artwork on one line
  // (extra-pet tier fee). "matched" = a matching set — one ordinary
  // single-pet line PER PET, full price each, NO fee (v7 §7: "A matching
  // set is N ordinary per-pet rows, unchanged from today").
  var currentIntent = "together";
  // The pets array from the last style-selected emission — the matched-set
  // submit builds its per-line properties from this, not from the flat
  // together-shaped form inputs.
  var currentPets = [];
  // Port Step 4 (Flow 2 library): reorder attribution from the previewer
  // ("library" | "library_pair" | null). Written as the CART ATTRIBUTE
  // `perkie_reorder_source` on a successful add (port brief §4). null means
  // organic and writes NOTHING — absence is the organic signal.
  var currentReorderSource = null;

  /**
   * Write the reorder-source cart attribute. Fire-and-forget with
   * keepalive so the single-pet native form submit (which navigates or
   * re-renders the drawer) cannot cancel it. Analytics-grade data: a
   * failed write is silently dropped, never surfaced to the customer.
   */
  function writeReorderAttribute() {
    if (!currentReorderSource) return;
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { perkie_reorder_source: currentReorderSource } }),
        keepalive: true,
      }).catch(function () { /* best-effort */ });
    } catch (e) { /* best-effort */ }
  }

  /**
   * Parse the fee variant data from the previewer element.
   * Priority: data-perkie-fee-variants JSON attribute (title-matched in Liquid).
   * Validates that parsed IDs look like valid Shopify variant IDs.
   */
  function parseFeeVariants() {
    var el = document.querySelector("[data-perkie-previewer]");
    if (!el) return;
    var raw = el.getAttribute("data-perkie-fee-variants");
    if (!raw) return;
    try {
      var parsed = JSON.parse(raw);
      // Validate: each value should be a positive integer (Shopify variant ID)
      var valid = true;
      for (var key in parsed) {
        if (parsed.hasOwnProperty(key)) {
          var id = parseInt(parsed[key], 10);
          if (!id || id <= 0) {
            console.warn("Perkie cart bridge: invalid fee variant ID for key '" + key + "':", parsed[key]);
            valid = false;
          }
        }
      }
      if (valid && Object.keys(parsed).length > 0) {
        feeVariantMap = parsed;
      } else if (!valid) {
        console.warn("Perkie cart bridge: fee variant map has invalid entries, multi-pet fees may not apply correctly");
        feeVariantMap = parsed; // Still use it, but warn
      }
    } catch (e) {
      console.error("Perkie cart bridge: failed to parse fee variant data:", e);
    }
  }

  /**
   * Track pet count from the previewer.
   */
  function onPetCountChanged(event) {
    var detail = event.detail;
    if (!detail || !detail.count) return;
    currentPetCount = detail.count;
    // 32-B: the flow sends photoCount beside count; an emitter that does
    // not (the legacy previewer) counts one photo per pet, as today.
    currentPhotoCount = typeof detail.photoCount === "number" && detail.photoCount > 0 ? detail.photoCount : detail.count;
  }

  /**
   * Intercept the product form submission to add the fee product
   * alongside the main product when multiple pets are selected.
   */
  function interceptFormSubmit(form) {
    if (!form) return;

    var productFormEl = form.closest("product-form");
    if (!productFormEl) return;

    // Override the product-form custom element's onSubmitHandler
    // by intercepting the form's submit event in the capture phase
    form.addEventListener("submit", function (event) {
      // D3-PW0: every pet on the piece is confirmed at the submit, first.
      markPetsConfirmedFromForm(form);
      var extraPets = currentPetCount - 1;
      // 32-B (R1): the fee variant is keyed on EXTRA PHOTOS. A split pair
      // is one photo: extraPhotos is 0 and no fee line attaches, while the
      // main line still carries two pets in its properties.
      var extraPhotos = Math.max(0, Math.min(currentPhotoCount, currentPetCount) - 1);

      // If only 1 pet, let normal flow handle it
      if (extraPets <= 0) return;

      // Port Step 3 (spec v7 §1.2/§7): a matching set is N ordinary
      // single-pet lines at full price with NO extra-pet fee — the fee
      // variant lookup below does not apply to it.
      var isMatchedSet = currentIntent === "matched" && currentPets.length > 1;

      // 32-B: several pets from ONE photo (a split pair) owe no fee and need
      // no intercept — the native single-line submit carries every pet's
      // properties already, exactly as the one-pet path does.
      if (!isMatchedSet && extraPhotos <= 0) return;

      var feeVariantId = null;
      if (!isMatchedSet) {
        feeVariantId = feeVariantMap[String(extraPhotos)];

        // If multi-pet but fee variant not found, warn the customer and log diagnostic info
        if (!feeVariantId) {
          console.error("Perkie cart bridge: fee variant not found for " + extraPhotos + " extra photo(s). Fee map:", JSON.stringify(feeVariantMap));
          var proceed = window.confirm(
            "Multi-pet pricing couldn't be applied automatically for " + (extraPets + 1) + " pets. " +
            "Your order will be added without the multi-pet fee. " +
            "Please contact us at hello@perkieprints.com if your total looks incorrect.\n\n" +
            "Click OK to continue, or Cancel to go back."
          );
          if (!proceed) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          // Let normal (single-product) form submission proceed
          return;
        }
      }

      // Prevent Shopify's default form submission
      event.preventDefault();
      event.stopImmediatePropagation();

      // Collect the main product's form data
      var formData = new FormData(form);
      var mainVariantId = formData.get("id");
      var mainQuantity = parseInt(formData.get("quantity") || "1", 10);

      // Build line item properties from all perkie hidden inputs (style + font)
      var properties = {};
      var inputs = form.querySelectorAll("[data-perkie-property], [data-perkie-font-property]");
      inputs.forEach(function (input) {
        // input.name is like "properties[Style]"
        var match = input.name.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = input.value;
        }
      });

      // Also include any non-perkie properties from the form
      for (var pair of formData.entries()) {
        if (pair[0].indexOf("properties[") === 0 && pair[0].indexOf("_perkie") === -1) {
          var propMatch = pair[0].match(/^properties\[(.+)\]$/);
          if (propMatch && !properties[propMatch[1]]) {
            properties[propMatch[1]] = pair[1];
          }
        }
      }

      // Build the items array for Cart API
      // Fee variant is listed first so the main product (added last)
      // appears at the top of Shopify's cart drawer.
      //
      // Normalize the bundle reference to JUST the /products/<handle> portion.
      // window.location.pathname can include a /collections/<handle>/ prefix when
      // the customer arrived from a collection page; if we store the prefixed
      // path here but cart.js returns the same URL (which it does), the comparison
      // in perkie-cart-fee.js sees a "match" — but ONLY when both sides agree on
      // the prefix. Customers who navigate later or whose cart returns a
      // collection-prefixed item.url cause a mismatch → fee gets reaped as
      // "orphaned" within ~300ms of ATC. Stripping to /products/<handle> on
      // BOTH sides eliminates the mismatch.
      // Artwork identity for fee reconciliation: sorted pet upload IDs + style.
      // Same pets, same style = same artwork = one editing job = one fee,
      // however many products carry it. Different style is a different artwork
      // (it is reviewed again before print), so it owes its own fee.
      function perkieArtworkKey(props) {
        var ids = [];
        for (var k in props) {
          if (/^_perkie_pet_\d+_upload_id$/.test(k) && props[k]) ids.push(props[k]);
        }
        if (!ids.length) return "";
        ids.sort();
        return ids.join(",") + "|" + (props["_perkie_style"] || "");
      }

      var bundleRefMatch = window.location.pathname.match(/\/products\/[^/]+/);
      var bundleRef = bundleRefMatch ? bundleRefMatch[0] : window.location.pathname;

      // ── Matching set (Port Step 3, spec v7 §1.2/§7) ────────────────────
      // N ordinary single-pet lines: each carries ONE pet's full property
      // set with _perkie_pet_count 1, so the webhook's per-line extraction
      // (one artwork = one upload+style pair) works unchanged. Shared
      // order-level values (Style/Font/session) come from the flat form
      // inputs; per-pet values come from the pets array of the last
      // style-selected emission. No fee item — each piece is full price
      // and carries one pet.
      if (isMatchedSet) {
        var matchedItems = currentPets.map(function (pet) {
          var props = {};
          ["Style", "Font", "_perkie_style", "_perkie_font", "_perkie_session_id"].forEach(function (k) {
            if (properties[k]) props[k] = properties[k];
          });
          props["Name"] = pet.name || "";
          if (pet.artistNotes) props["Notes"] = pet.artistNotes;
          props["_perkie_pet_count"] = "1";
          props["_perkie_pet_1_upload_id"] = pet.uploadId || "";
          props["_perkie_pet_1_original_url"] = pet.originalUrl || "";
          props["_perkie_pet_1_preview_url"] = pet.previewUrl || "";
          if (pet.isExistingPrint && pet.existingOrderNumber) {
            props["Previous Order"] = pet.existingOrderNumber;
            props["_perkie_pet_1_previous_order"] = pet.existingOrderNumber;
            props["_perkie_order_type"] = "returning";
          }
          // Analytics/series marker + set size, so the order shows the
          // lines belong together. Hidden (underscore) — the visible cart
          // simply shows N lines of the product, which is the truth.
          props["_perkie_composition"] = "matched";
          props["_perkie_matched_set"] = String(currentPets.length);
          return {
            id: parseInt(mainVariantId, 10),
            quantity: mainQuantity,
            properties: props,
          };
        });
        postCartItems(matchedItems, form);
        return;
      }

      var items = [
        {
          id: parseInt(feeVariantId, 10),
          quantity: 1,
          properties: {
            "_perkie_bundled_with": bundleRef,
            // How many EXTRA pets this fee unit covers. Lets
            // perkie-cart-fee.js reconcile fee quantity against the number of
            // surviving multi-pet parents on the same product path, instead of
            // only reaping fully-orphaned lines. Without it, two adds of the
            // SAME product merge into one fee line at qty 2 (the properties are
            // otherwise byte-identical), and removing one parent left the whole
            // qty-2 line in place — a real overcharge. Fee lines added before
            // this tag existed fall back to the path-only rule. Since 32-B
            // this is the EXTRA PHOTO count the unit covers (R1); with no
            // split in the session it equals the extra pet count as before.
            "_perkie_fee_extra": String(extraPhotos),
            // Which ARTWORK this fee unit covers: the sorted set of pet upload
            // IDs plus the style. The fee pays for the artist editing the extra
            // image, and that happens ONCE per artwork — so two products
            // carrying the same pets in the same style owe ONE fee, not two
            // (Corey, 2026-08-11). perkie-cart-fee.js reconciles on this key.
            // Empty when upload IDs are unavailable; the reconciler then falls
            // back to the older per-product-path rule rather than guessing.
            "_perkie_fee_artwork": perkieArtworkKey(properties),
          },
        },
        {
          id: parseInt(mainVariantId, 10),
          quantity: mainQuantity,
          properties: properties,
        },
      ];

      postCartItems(items, form);
    }, true); // capture phase to run before Shopify's handler
  }

  /**
   * Port Step 51 (D37): the D32 card owns this add's failure only on the D32
   * phone screen: html.perkie-d32 (the card hides Dawn's form and adds
   * through it; never on a matched set) and under 750, read at the failure,
   * with Dawn's pub/sub there to tell it. D32 off, 750 and up and the
   * matched set keep the alert (Corey, PM-351: "leave it for now").
   */
  function d37CardSaysIt() {
    try {
      return document.documentElement.classList.contains("perkie-d32")
        && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 749px)").matches
        && typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && !!PUB_SUB_EVENTS && !!PUB_SUB_EVENTS.cartError;
    } catch (e) {
      return false;
    }
  }

  /**
   * POST an items array to the Cart API with drawer refresh + analytics.
   * Shared by the together path (fee + main item) and the matched-set path
   * (N single-pet items) — extracted in Port Step 3 so the two line shapes
   * ride one tested submit pipeline.
   */
  function postCartItems(items, form) {
      // Disable the submit button while adding
      var btn = getSubmitButton(form);
      if (btn) {
        btn.disabled = true;
        var span = btn.querySelector("span");
        if (span) span.textContent = "Adding...";
      }

      // Find the cart drawer element for section rendering
      var cartDrawer = document.querySelector("cart-drawer");

      // Build the request body — include sections for drawer refresh
      var requestBody = { items: items };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        requestBody.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
        requestBody.sections_url = window.location.pathname;
      }

      // Port Step 51 (D37): a response that was not ok (a refusal) vs a
      // fetch that rejected (dropped), for the D32 card below.
      var refused = false;

      // POST to Cart API
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })
        .then(function (response) {
          if (!response.ok) {
            refused = true;
            return response.json().then(function (err) {
              throw new Error(err.description || err.message || "Could not add to cart");
            });
          }
          return response.json();
        })
        .then(function (responseData) {
          // Step 4: the add succeeded — stamp the reorder-source cart
          // attribute (no-op when null/organic).
          writeReorderAttribute();

          // ── Analytics: track add-to-cart ──
          var formEl = getForm();
          if (window.__perkieAnalytics && window.__perkieAnalytics.trackAddToCart) {
            var styleInput = formEl ? formEl.querySelector('input[name="properties[_perkie_style]"]') : null;
            var fontInput = formEl ? formEl.querySelector('input[name="properties[_perkie_font]"]') : null;
            var petCountInput = formEl ? formEl.querySelector('input[name="properties[_perkie_pet_count]"]') : null;
            var count = petCountInput ? parseInt(petCountInput.value, 10) : 1;
            var previewerEl = document.querySelector("[data-perkie-previewer]");
            var nameInput = formEl ? (formEl.querySelector('input[name="properties[Name]"]')
              || formEl.querySelector('input[name="properties[Pet 1 Name]"]')) : null;
            window.__perkieAnalytics.trackAddToCart({
              style: styleInput ? styleInput.value : "",
              productId: previewerEl ? previewerEl.getAttribute("data-perkie-product-id") : "",
              petCount: count,
              hasName: !!(nameInput && nameInput.value),
              hasFont: !!(fontInput && fontInput.value),
              // Port step 41 (D29-A rider A-12): the font on the line.
              font: fontInput ? fontInput.value : "",
            });
          }

          // Open the cart drawer directly
          if (cartDrawer && responseData.sections) {
            if (cartDrawer.classList.contains("is-empty")) {
              cartDrawer.classList.remove("is-empty");
            }
            cartDrawer.renderContents(responseData);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || "/cart";
          }

          // Publish cart update event for any other listeners
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              cartData: responseData,
            });
          }
        })
        .catch(function (err) {
          console.error("Perkie cart bridge: failed to add items", err);
          // Port Step 51 (D37; Corey PM-351, PM-353): on the D32 phone screen
          // the card says it in its own status line, so no alert there: the
          // failure goes to the card as Dawn's cart-error contract, with the
          // piece's variant (the LAST item, the one the card wrote) and
          // kind "refused" (a response that was not ok) or "dropped" (the
          // fetch rejected). Everywhere else, the alert as before.
          if (d37CardSaysIt()) {
            try {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "perkie-cart-bridge",
                productVariantId: String(items[items.length - 1].id),
                kind: refused ? "refused" : "dropped",
                message: err && err.message ? err.message : "",
              });
              return;
            } catch (e) {
              console.error("Perkie cart bridge: the card could not take the failure", e);
            }
          }
          alert("Could not add to cart: " + err.message);
        })
        .finally(function () {
          // Re-enable the submit button
          if (btn) {
            btn.disabled = false;
            var span = btn.querySelector("span");
            if (span) span.textContent = btn.getAttribute("data-perkie-original-text") || "Add to cart";
          }
        });
  }

  /**
   * Attach a bubble-phase submit listener that fires perkie_add_to_cart
   * for single-pet orders. Multi-pet orders already fire this event inside
   * interceptFormSubmit (capture phase) which calls stopImmediatePropagation,
   * so this listener will never double-fire.
   */
  function attachAddToCartAnalytics(form) {
    if (!form || form.hasAttribute("data-perkie-atc-analytics")) return;
    form.setAttribute("data-perkie-atc-analytics", "true");

    form.addEventListener("submit", function () {
      // D3-PW0: single-pet native path (the capture intercept let it through).
      markPetsConfirmedFromForm(form);
      var stylePresent = form.querySelector('input[name="properties[_perkie_style]"]');
      // Step 4: single-pet orders submit natively (this bubble listener only
      // runs when the capture-phase multi-pet intercept let the submit
      // through), so the attribute write must survive the page action —
      // writeReorderAttribute uses keepalive for exactly this path.
      if (stylePresent && stylePresent.value) writeReorderAttribute();

      if (!window.__perkieAnalytics || !window.__perkieAnalytics.trackAddToCart) return;
      var styleInput = form.querySelector('input[name="properties[_perkie_style]"]');
      if (!styleInput || !styleInput.value) return; // Not a perkie order
      var fontInput = form.querySelector('input[name="properties[_perkie_font]"]');
      var petCountInput = form.querySelector('input[name="properties[_perkie_pet_count]"]');
      var previewerEl = document.querySelector("[data-perkie-previewer]");
      var nameInput = form.querySelector('input[name="properties[Name]"]')
        || form.querySelector('input[name="properties[Pet 1 Name]"]');
      window.__perkieAnalytics.trackAddToCart({
        style: styleInput.value,
        productId: previewerEl ? previewerEl.getAttribute("data-perkie-product-id") : "",
        petCount: petCountInput ? parseInt(petCountInput.value, 10) : 1,
        hasName: !!(nameInput && nameInput.value),
        hasFont: !!(fontInput && fontInput.value),
        // Port step 41 (D29-A rider A-12): the font on the line.
        font: fontInput ? fontInput.value : "",
      });
    }); // bubble phase — capture-phase handler blocks this for multi-pet
  }

  // ── Event Handlers ─────────────────────────────────────────────────

  /**
   * Handle style-selected: inject all line item properties.
   */
  function onStyleSelected(event) {
    var detail = event.detail;
    if (!detail || !detail.style) return;

    var form = getForm();
    if (!form) { reportFormNotFound("onStyleSelected"); return; }

    var container = getContainer(form);

    // Clear existing properties
    clearProperties(form);

    // Track pet count for fee product logic
    var pets = detail.pets || [];
    if (pets.length > 0) {
      currentPetCount = pets.length;
      // 32-B (R1): distinct source photos, from detail.photoCount when the
      // flow sends it, else from the pets' sourcePhotoId / uploadId.
      currentPhotoCount = typeof detail.photoCount === "number" && detail.photoCount > 0
        ? detail.photoCount
        : photoCountFromPets(pets);
    }

    // Port Step 3 (spec v7 §1.2): capture intent + pets for the submit
    // intercept. Intent rides EVERY style-selected emission (including the
    // quiet re-broadcasts on intent change and pet removal), so the bridge
    // can never hold a stale line shape.
    currentIntent = detail.intent === "matched" && pets.length > 1 ? "matched" : "together";
    currentPets = pets;
    // Step 4: reorder attribution rides every emission (including quiet
    // re-broadcasts) for the same no-stale-state reason as intent above.
    currentReorderSource = detail.reorderSource || null;

    var styleLabel = detail.styleLabel || STYLE_LABELS[detail.style] || detail.style;
    var inputs = [];

    // ── Visible properties (shown to customer in cart + to you in admin) ──
    inputs.push(createInput("Style", styleLabel));
    inputs.push(createInput("Font", detail.fontKey || ""));

    // Track if any pet is a returning customer existing print
    var hasExistingPrint = pets.some(function (pet) { return pet.isExistingPrint; });

    pets.forEach(function (pet, index) {
      var n = index + 1;
      var prefix = pets.length > 1 ? "Pet " + n + " " : "";
      inputs.push(createInput(prefix + "Name", pet.name || ""));
      if (pet.isExistingPrint && pet.existingOrderNumber) {
        inputs.push(createInput(prefix + "Previous Order", pet.existingOrderNumber));
      }
      if (pet.artistNotes) {
        inputs.push(createInput(prefix + "Notes", pet.artistNotes));
      }
    });

    // ── Hidden properties (underscore prefix — hidden from customer) ──
    if (detail.sessionId) {
      inputs.push(createInput("_perkie_session_id", detail.sessionId));
    }
    inputs.push(createInput("_perkie_style", detail.style));
    inputs.push(createInput("_perkie_pet_count", String(pets.length)));
    // 32-B (R1): the distinct source-photo count the fee was keyed on —
    // additive, hidden; the webhook keeps reading pets from _perkie_pet_count.
    inputs.push(createInput("_perkie_photo_count", String(currentPhotoCount)));
    // Generation-series marker (spec v7 §3). "perpet" on the flat
    // (together-shaped) inputs — matched-set lines are rebuilt per pet at
    // submit and stamp their own "matched". Port Step 4.5: a COMBINED
    // emission (detail.composition === "combined", only ever sent by the
    // flag-gated combined path — inert today) stamps "combined" plus the
    // two artwork properties the webhook contract guards make mandatory on
    // combined lines: _perkie_comb_id and _perkie_combined_preview_url.
    // Written ONLY when both values are present — a combined marker with a
    // missing artwork id would be exactly the empty-property class order
    // #37259 shipped with.
    if (detail.composition === "combined" && detail.combId && detail.combinedPreviewUrl) {
      inputs.push(createInput("_perkie_composition", "combined"));
      inputs.push(createInput("_perkie_comb_id", detail.combId));
      inputs.push(createInput("_perkie_combined_preview_url", detail.combinedPreviewUrl));
    } else {
      inputs.push(createInput("_perkie_composition", "perpet"));
    }
    if (hasExistingPrint) {
      inputs.push(createInput("_perkie_order_type", "returning"));
    }

    pets.forEach(function (pet, index) {
      var n = index + 1;
      inputs.push(createInput("_perkie_pet_" + n + "_upload_id", pet.uploadId || ""));
      inputs.push(createInput("_perkie_pet_" + n + "_original_url", pet.originalUrl || ""));
      inputs.push(createInput("_perkie_pet_" + n + "_preview_url", pet.previewUrl || ""));
      if (pet.isExistingPrint && pet.existingOrderNumber) {
        inputs.push(createInput("_perkie_pet_" + n + "_previous_order", pet.existingOrderNumber));
      }
    });

    // Append all inputs to the container
    inputs.forEach(function (input) {
      container.appendChild(input);
    });

    // Enable the Add to Cart button
    enableSubmit(form);

    // Update the visual indicator
    updateStyleIndicator(form, detail);

    // Scroll to and pulse the Add to Cart button so the customer sees it's
    // ready. SKIPPED for quiet re-broadcasts (intent change, pet removal):
    // those are settings updates, not commits — yanking the viewport to the
    // ATC button there is the focus-steal spec v7's riders forbid.
    if (!detail.quiet) {
      scrollToAndPulseSubmit(form);
    }
  }

  /**
   * Handle previewer-reset: clear everything.
   */
  function onPreviewerReset() {
    // Step 4: a reset invalidates the configuration the attribution
    // described; the next style-selected re-derives it.
    currentReorderSource = null;

    var form = getForm();
    if (!form) { reportFormNotFound("onPreviewerReset"); return; }

    clearProperties(form);
    disableSubmit(form, gateLabel());
    hideStyleIndicator(form);
  }

  /**
   * Handle previewer-ready: initial setup on product pages.
   * Disables Add to Cart until a style is selected.
   */
  function onPreviewerReady() {
    var form = getForm();
    if (!form) { reportFormNotFound("onPreviewerReady"); return; }

    // Only gate the button if there aren't already perkie properties
    // (handles the case where style-selected fires before ready)
    var existing = form.querySelector("[data-perkie-property]");
    if (!existing) {
      disableSubmit(form, gateLabel());
    }

    // Parse fee variant data and set up form submission intercept
    parseFeeVariants();
    if (Object.keys(feeVariantMap).length > 0) {
      interceptFormSubmit(form);
    }

    // Track perkie_add_to_cart for single-pet orders (all previewer-originated ATC)
    attachAddToCartAnalytics(form);
  }

  /**
   * Handle font-selected: inject/update font properties.
   */
  function onFontSelected(event) {
    var form = getForm();
    if (!form) { reportFormNotFound("onFontSelected"); return; }

    var detail = event.detail || {};
    var container = getContainer(form);

    // Remove existing font inputs
    var existing = container.querySelectorAll("[data-perkie-font-property]");
    existing.forEach(function (el) { el.remove(); });

    // Update the visible Font property (set during style-selected)
    var visibleFont = container.parentNode.querySelector('input[name="properties[Font]"]');
    if (visibleFont) {
      visibleFont.value = detail.fontLabel || detail.font;
    } else {
      var fontVisInput = createInput("Font", detail.fontLabel || detail.font);
      fontVisInput.setAttribute("data-perkie-font-property", "true");
      container.appendChild(fontVisInput);
    }

    // Hidden font key for fulfillment
    var fontKeyInput = createInput("_perkie_font", detail.font);
    fontKeyInput.setAttribute("data-perkie-font-property", "true");
    container.appendChild(fontKeyInput);
  }

  // Artist notes are now per-pet, included in the perkie:style-selected event detail.
  // No separate handler needed.

  // ── Init ───────────────────────────────────────────────────────────

  function init() {
    // Listen for previewer events
    window.addEventListener("perkie:style-selected", onStyleSelected);
    window.addEventListener("perkie:previewer-reset", onPreviewerReset);
    window.addEventListener("perkie:previewer-ready", onPreviewerReady);
    window.addEventListener("perkie:pet-count-changed", onPetCountChanged);
    window.addEventListener("perkie:font-selected", onFontSelected);
    window.addEventListener("perkie:progress", onProgress); // F1 stage-aware gate copy
    // Artist notes are now per-pet in style-selected detail — no separate listener needed.

    // 2026-05-11 fix (Audit 1): Shopify variant-change silently re-enables
    // the ATC button and resets text to "Add to cart" via
    // product-form.js::toggleSubmitButton(false) called from
    // product-info.js::handleUpdateProductInfo. That overrides our
    // disableSubmit() gate, letting customers who haven't completed the
    // previewer add a property-less line item to the cart by toggling
    // variants. Re-gate on every variant change.
    //
    // pubsub.js + constants.js are loaded in theme.liquid (lines 110-111)
    // with `defer` BEFORE this script (loaded in main-product.liquid line 790),
    // so `subscribe` and `PUB_SUB_EVENTS` are always defined by the time
    // init() runs. The typeof guards are defense-in-depth in case load
    // order changes.
    if (typeof subscribe === "function"
        && typeof PUB_SUB_EVENTS === "object"
        && PUB_SUB_EVENTS
        && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        var form = getForm();
        if (!form) { reportFormNotFound("onVariantChange"); return; }
        // If Shopify already disabled the button (e.g. the new variant is
        // sold out — toggleSubmitButton(true, "Sold out")), respect that.
        // Overwriting with our "Upload a photo to continue" copy would
        // mask the real reason. Sold-out variants disable the button
        // before publishing variant-change, so this check runs after the
        // disabled state is set.
        var btn = getSubmitButton(form);
        if (btn && btn.disabled) return;
        // If perkie properties already exist (customer completed the
        // previewer before changing variants), leave the button alone —
        // Shopify's toggleSubmitButton already enabled it correctly and
        // the existing hidden inputs travel with whatever variant the
        // customer picks. We only need to re-gate when no properties
        // exist yet.
        var hasProps = !!form.querySelector("[data-perkie-property]");
        if (!hasProps) {
          disableSubmit(form, gateLabel());
        }
      });
    }

    // Step 4 (Flow 2): write the reorder-source cart attribute AFTER a
    // successful add. The submit-time keepalive write alone is not enough:
    // on Dawn's AJAX path it races ahead of /cart/add, and Shopify drops
    // attributes written to an EMPTY cart when the first item creates the
    // real cart (observed live 2026-08-18: update.js landed 168ms before
    // /cart/add and the attribute vanished). cartUpdate publishes only
    // after the add succeeds, so the cart is never empty here. Same
    // load-order guarantee as the variantChange subscription above.
    if (typeof subscribe === "function"
        && typeof PUB_SUB_EVENTS === "object"
        && PUB_SUB_EVENTS
        && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        writeReorderAttribute();
      });
    }

    // Check if previewer already fired ready before this script loaded
    // (the previewer section loads before buy-buttons)
    var form = getForm();
    if (form) {
      var previewerEl = document.querySelector("[data-perkie-previewer]");
      if (previewerEl) {
        var mode = previewerEl.getAttribute("data-perkie-previewer");
        if (mode === "product") {
          // Parse fee variants and set up intercept even if we missed
          // the perkie:previewer-ready event (race condition fix)
          parseFeeVariants();
          if (Object.keys(feeVariantMap).length > 0) {
            interceptFormSubmit(form);
          }

          // Check if properties already exist (style-selected already fired)
          var existing = form.querySelector("[data-perkie-property]");
          if (!existing) {
            disableSubmit(form, gateLabel());
          }

          // Track perkie_add_to_cart for single-pet orders
          attachAddToCartAnalytics(form);
        }
      }
    }
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
